import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, throwError, map, of } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { Chatbot, ChatbotsResponse, UpdateChatbotResponse } from '../models/chatbot.model';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';
import { getSecureRandomInt } from '../utils/secure-random.util';

@Injectable({
  providedIn: 'root'
})
export class ChatbotManagementService {
  private http = inject(HttpClient);
  private oauthService = inject(OAuthService, { optional: true });
  private authService = inject(AuthService);
  
  // Endpoint base desde environment
  private readonly baseUrl = environment.userManagementApiUrl;
  private readonly chatbotsEndpoint = `${this.baseUrl}/chatbot`;

  private currentUsername: string = '';

  constructor() {
    // Suscribirse al usuario autenticado para obtener el email
    this.authService.user$.subscribe(user => {
      if (user && user.email) {
        this.currentUsername = user.email;
      }
    });
  }

  /**
   * Get all available chatbots
   */
  getAllChatbots(): Observable<Chatbot[]> {
    if (environment.mockAuth) {
      return this.getMockChatbots();
    }
    const headers = this.authService.getAuthHeaders();
    
    return this.http.get<ChatbotsResponse>(this.chatbotsEndpoint, { headers }).pipe(
      map(response => response.chatbots),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockChatbots(): Observable<Chatbot[]> {
    const mockChatbots: Chatbot[] = [
      {
        chatbotId: 1,
        name: 'Test Chatbot 1',
        code: 'test-bot-1',
        description: 'First test chatbot',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedBy: null,
        endpointToSend: 'https://api.test.com/send',
        endpointToReceive: 'https://api.test.com/receive',
        credentials: null,
        typeId: 1,
        conversationVisualizerEndpoint: null,
        conversationVisualizerCredentials: null,
      },
      {
        chatbotId: 2,
        name: 'Test Chatbot 2',
        code: 'test-bot-2',
        description: 'Second test chatbot',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedBy: null,
        endpointToSend: 'https://api.test.com/send2',
        endpointToReceive: 'https://api.test.com/receive2',
        credentials: null,
        typeId: 1,
        conversationVisualizerEndpoint: null,
        conversationVisualizerCredentials: null,
      },
      {
        chatbotId: 3,
        name: 'Test Chatbot 3',
        code: 'test-bot-3',
        description: 'Third test chatbot',
        isActive: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin',
        updatedBy: null,
        endpointToSend: 'https://api.test.com/send3',
        endpointToReceive: 'https://api.test.com/receive3',
        credentials: null,
        typeId: 1,
        conversationVisualizerEndpoint: null,
        conversationVisualizerCredentials: null,
      }
    ];
    return of(mockChatbots);
  }

  /**
   * Create new chatbot
   */
  createChatbot(chatbotData: Partial<Chatbot>): Observable<Chatbot> {
    if (environment.mockAuth) {
      return this.getMockCreatedChatbot(chatbotData);
    }
    
    const headers = this.authService.getAuthHeaders();
    
    const payload: any = {
      code: chatbotData.code,
      name: chatbotData.name,
      description: chatbotData.description,
      endpointToSend: chatbotData.endpointToSend,
      endpointToReceive: chatbotData.endpointToReceive,
      credentials: chatbotData.credentials,
      typeId: chatbotData.typeId,
      operatedBy: this.currentUsername,
      isActive: chatbotData.isActive,
      conversationVisualizerEndpoint: chatbotData.conversationVisualizerEndpoint,
      conversationVisualizerCredentials: chatbotData.conversationVisualizerCredentials,
      conversationVisualizerParams: (chatbotData as any).conversationVisualizerParams
    };
          
    return this.http.post<UpdateChatbotResponse>(this.chatbotsEndpoint, payload, { headers }).pipe(
      map(response => response.chatbot),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockCreatedChatbot(chatbotData: Partial<Chatbot>): Observable<Chatbot> {
    const mockChatbot: Chatbot = {
      chatbotId: getSecureRandomInt(100, 1100),
      code: chatbotData.code || 'new-bot',
      name: chatbotData.name || 'New Chatbot',
      description: chatbotData.description || 'A new test chatbot',
      isActive: chatbotData.isActive ?? true,
      endpointToSend: chatbotData.endpointToSend || '',
      endpointToReceive: chatbotData.endpointToReceive || '',
      credentials: chatbotData.credentials || null,
      typeId: chatbotData.typeId || 1,
      conversationVisualizerEndpoint: chatbotData.conversationVisualizerEndpoint || null,
      conversationVisualizerCredentials: chatbotData.conversationVisualizerCredentials || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: this.currentUsername,
      updatedBy: null,
    };
    return of(mockChatbot);
  }

  /**
   * Update chatbot
   */
  updateChatbot(chatbotId: number, chatbotData: Partial<Chatbot>): Observable<Chatbot> {
    if (environment.mockAuth) {
      return this.getMockUpdatedChatbot(chatbotId, chatbotData);
    }
    
    const headers = this.authService.getAuthHeaders();
    const url = `${this.chatbotsEndpoint}/${chatbotId}`;
    
    const payload: any = {
      code: chatbotData.code,
      name: chatbotData.name,
      description: chatbotData.description,
      endpointToSend: chatbotData.endpointToSend,
      endpointToReceive: chatbotData.endpointToReceive,
      credentials: chatbotData.credentials,
      typeId: chatbotData.typeId,
      operatedBy: this.currentUsername,
      isActive: chatbotData.isActive,
      conversationVisualizerEndpoint: chatbotData.conversationVisualizerEndpoint,
      conversationVisualizerCredentials: chatbotData.conversationVisualizerCredentials,
      conversationVisualizerParams: (chatbotData as any).conversationVisualizerParams
    };
    
    return this.http.put<UpdateChatbotResponse>(url, payload, { headers }).pipe(
      map(response => response.chatbot),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  private getMockUpdatedChatbot(chatbotId: number, chatbotData: Partial<Chatbot>): Observable<Chatbot> {
    const mockChatbot: Chatbot = {
      chatbotId,
      code: chatbotData.code || 'updated-bot',
      name: chatbotData.name || 'Updated Chatbot',
      description: chatbotData.description || 'An updated test chatbot',
      isActive: chatbotData.isActive ?? true,
      endpointToSend: chatbotData.endpointToSend || '',
      endpointToReceive: chatbotData.endpointToReceive || '',
      credentials: chatbotData.credentials || null,
      typeId: chatbotData.typeId || 1,
      conversationVisualizerEndpoint: chatbotData.conversationVisualizerEndpoint || null,
      conversationVisualizerCredentials: chatbotData.conversationVisualizerCredentials || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'admin',
      updatedBy: this.currentUsername,
    };
    return of(mockChatbot);
  }
}

